import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Download, Eye, FileText, FolderLock, ShieldCheck } from "lucide-react";
import { api, errMsg, fmtDate } from "@/lib/api";
import DocumentViewer, { downloadDocument } from "@/components/DocumentViewer";

export default function PartageDocuments() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState("");
  const [apercu, setApercu] = useState(null);

  useEffect(() => {
    api
      .get(`/partage/${token}`)
      .then(({ data }) => setData(data))
      .catch((e) => setErreur(errMsg(e, "Lien invalide")));
  }, [token]);

  const docUrl = (doc) => `/partage/${token}/documents/${doc.id}/download`;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white px-5 py-10">
      <div className="max-w-2xl mx-auto space-y-8">
        <div>
          <div className="font-display text-xl font-extrabold tracking-tighter">
            BK<span className="text-[#005CFF]">PROD</span>
          </div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-white/40 mt-1">Coffre-fort documents</div>
        </div>

        {erreur && (
          <div className="rounded-2xl border border-red-500/40 bg-red-500/10 p-6 text-sm" data-testid="partage-erreur">
            {erreur}
          </div>
        )}

        {data && (
          <div className="space-y-6" data-testid="partage-page">
            <div>
              <h1 className="font-display text-3xl font-extrabold tracking-tighter">{data.chantier}</h1>
              <p className="text-sm text-white/60 mt-2 flex items-center gap-2">
                <ShieldCheck size={15} /> Accès en lecture · expire le {fmtDate(data.expire_le)}
              </p>
              {data.libelle && <p className="text-sm text-white/50 mt-1">{data.libelle}</p>}
              <p className="text-[11px] text-white/35 mt-3">Chaque consultation ou téléchargement est enregistré dans le journal d'accès de BKPRO.</p>
            </div>

            {data.documents.length === 0 && (
              <div className="rounded-2xl border border-dashed border-white/20 p-10 text-center text-white/50 text-sm">
                <FolderLock size={24} className="mx-auto mb-3" /> Aucun document disponible pour ce chantier.
              </div>
            )}

            <div className="space-y-3">
              {data.documents.map((d) => (
                <div
                  key={d.id}
                  data-testid={`partage-doc-${d.id}`}
                  className="flex items-center gap-4 rounded-xl border border-white/10 bg-white/5 px-4 py-4 hover:border-[#005CFF] transition-colors"
                >
                  <FileText size={18} className="text-[#005CFF] shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold truncate">{d.titre}</div>
                    <div className="text-xs text-white/50 truncate">
                      {Math.round((d.size || 0) / 1024)} Ko · {fmtDate(d.created_at)}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {d.previewable && (
                      <button
                        data-testid={`partage-preview-${d.id}`}
                        onClick={() => setApercu(d)}
                        className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full bg-white/10 hover:bg-[#005CFF] transition-colors"
                      >
                        <Eye size={14} /> <span className="hidden sm:inline">Aperçu</span>
                      </button>
                    )}
                    <button
                      data-testid={`partage-download-${d.id}`}
                      onClick={() => downloadDocument(docUrl(d), d.filename)}
                      className="p-2 rounded-full text-white/40 hover:text-white hover:bg-white/10 transition-colors"
                    >
                      <Download size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {apercu && <DocumentViewer doc={apercu} url={docUrl(apercu)} onClose={() => setApercu(null)} />}
    </div>
  );
}
